import React, { useMemo } from 'react';

import Plus from '@ast/magma/components/icon/icons/Plus';

import {
  ActionGroup,
  ActionGroupItem,
} from '@app/common/components/ActionGroup';
import { FieldInfoGroupField, HiddenField } from '@app/common/configurable-wizards';
import { Field, FieldComponent, FieldLocator } from '@app/common/configurable-wizards/types';
import { combineNames, isFieldInfoGroupField } from '@app/common/configurable-wizards/utils';
import { isTruthy } from '@app/common/utils';

import { SubmitModal } from '@app/widgets/contacts-and-settings/components/SubmitModal';
import { useSettingFieldsContext } from '@app/widgets/contacts-and-settings/contexts/SettingFieldsContext';
import { useContactPermissions } from '@app/widgets/contacts-and-settings/hooks/useContactPermissions';
import { useDetailsSectionNavigator } from '@app/widgets/contacts-and-settings/hooks/useDetailsSectionNavigator';
import { useUserSettingsTranslation } from '@app/widgets/contacts-and-settings/hooks/useUserSettingsTranslation';

import { EmptyFields, PhoneFields } from '../../types';
import { shiftField } from '../../utils/fieldShift';
import { setFieldValues } from '../../utils/fieldValues';

import { phoneFieldsListGroupsMeta as groupsMeta } from './constants';
import styles from './PhoneFieldsList.pcss';

export const PhoneFieldsList: FieldComponent<FieldInfoGroupField> = ({ parent, field, locator }) => {
  const { t } = useUserSettingsTranslation();
  const { navigate } = useDetailsSectionNavigator();
  const { emptyFields } = useSettingFieldsContext();
  const { editAllowed } = useContactPermissions(field);

  const contactType = PhoneFields[field.id as keyof typeof PhoneFields];
  const currentGroup = groupsMeta[contactType];

  const emptyFieldGroup = useMemo(() => {
    const data = emptyFields?.[EmptyFields[currentGroup.type]] as FieldInfoGroupField;
    const primary = !field.subFields.length;

    return data && setFieldValues(data, {
      IsPrimary: String(primary),
      Enabled: 'true',
    });
  }, [field, emptyFields, currentGroup]);

  const subFields = useMemo(() => shiftField(
    field.subFields as Field[],
    (item) => {
      const { id, sValue } = item as HiddenField;
      return id === 'IsPrimary' && isTruthy(sValue);
    },
    0,
  ), [field]);

  if (subFields.length === 0 && !editAllowed) {
    return null;
  }

  return (
    <div className={styles.group}>
      <h3 className={styles.title}>
        {currentGroup.title}
      </h3>
      <ActionGroup>
        {subFields.map((item, index) => {
          const subField = item as FieldInfoGroupField;
          const Component = locator(subField);

          return (
            <ActionGroupItem
              className={styles.groupItem}
              iconClassName={styles.groupItemIcon}
              key={subField.id || index}
              onSelect={() => navigate(subField)}
            >
              <Component
                parent={combineNames(parent, field.id)}
                field={subField}
                locator={locator}
              />
            </ActionGroupItem>
          );
        })}
      </ActionGroup>
      {emptyFieldGroup && editAllowed && (
        <SubmitModal
          field={emptyFieldGroup}
          successMessage={currentGroup?.submitMessage}
          actionButton={(
            <ActionGroupItem
              data-stable-name="AddPhoneNumberButton"
              className={styles.submitPhone}
              iconClassName={styles.hidden}
            >
              <Plus className={styles.submitPhoneIcon} />
              {t(
                'phone-fields-list.submit-phone.action-button|Submit new phone number action button',
                'Add phone number',
              )}
            </ActionGroupItem>
          )}
          dialogOptions={{
            title: t(
              'phone-fields-list.submit-dialog.title|Submit new phone number dialog title',
              'Add new phone number',
            ),
            showClose: {
              // eslint-disable-next-line i18next/no-literal-string
              'aria-label': t(
                'phone-fields-list.submit-dialog.close-button.aria-label|Accessibility text for close button of submit phone number dialog',
                'Close new phone dialog',
              ),
            },
          }}
        />
      )}
    </div>
  );
};

export const PhoneFieldsListLocator: FieldLocator = ({ field }) => (
  isFieldInfoGroupField(field) && field.id in PhoneFields
    ? PhoneFieldsList
    : undefined
);
